import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, routeForUser } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Loader2,
  Shield,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 20 — Public parent invitation landing.
 *
 * Reads the ``?token=<jwt>`` query param, decodes it against
 * ``/api/parent/invite/decode``, and lets the parent accept or reject.
 * Accepting hits ``/api/parent/accept-invitation`` and (on success)
 * saves the JWT + routes to ``/parent``.
 */
export default function ParentInvite() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const token = sp.get("token") || "";

  const [decoding, setDecoding] = useState(true);
  const [decoded, setDecoded] = useState(null);
  const [error, setError] = useState(null);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rejected, setRejected] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This link is missing a token — check that the URL is complete.");
      setDecoding(false);
      return;
    }
    api
      .get(`/parent/invite/decode?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setDecoded(data);
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "invalid_or_expired_token";
        const map = {
          invalid_or_expired_token:
            "This invitation link is invalid or expired.",
          invitation_no_longer_valid:
            "This invitation is no longer valid (already accepted or cancelled).",
        };
        setError(map[detail] || detail);
      })
      .finally(() => setDecoding(false));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!password || password.length < 8)
      return toast.error("Password must be at least 8 characters.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/parent/accept-invitation", {
        token,
        name: name.trim(),
        password,
      });
      setAuth(data.access_token, data.user);
      toast.success("Welcome! Redirecting to your parent dashboard…");
      navigate(routeForUser(data.user), { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "accept_failed";
      const map = {
        invalid_credentials:
          "Wrong password for your existing account. Try again.",
        account_uses_google_login:
          "This email is linked to a Google login. Sign in on the home page and open the invite link again.",
        password_required_for_existing_account:
          "This email already has an account — enter its password.",
      };
      toast.error(map[detail] || (typeof detail === "string" ? detail : "Accept failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const reject = async () => {
    if (!window.confirm("Reject this invitation? Your child will be notified.")) return;
    setSubmitting(true);
    try {
      await api.post("/parent/reject-invitation", { token });
      setRejected(true);
    } catch {
      toast.error("Couldn't reject — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900 flex flex-col"
      data-testid="parent-invite-page"
    >
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-amber-500 text-stone-900 flex items-center justify-center">
              <KeyRound className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold tracking-tight">KKS</div>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <Card className="w-full max-w-md border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-4 w-4 text-amber-600" />
              Parent invitation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {decoding && (
              <div className="py-8 text-center text-sm text-stone-500">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </div>
            )}

            {!decoding && error && (
              <div
                className="rounded-md border border-red-200 bg-red-50/60 p-3 text-sm text-red-900 flex items-start gap-2"
                data-testid="parent-invite-error"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {!decoding && rejected && (
              <div
                className="rounded-md border border-stone-200 bg-stone-50 p-4 text-sm text-stone-800 space-y-2"
                data-testid="parent-invite-rejected"
              >
                <div className="flex items-center gap-2 font-medium text-stone-900">
                  <XCircle className="h-4 w-4 text-stone-500" /> Invitation
                  declined
                </div>
                <p>
                  Thanks — we've told your child. If this was a mistake, they
                  can invite you again from their KKS profile.
                </p>
              </div>
            )}

            {!decoding && decoded && !rejected && (
              <>
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm space-y-1">
                  <div className="text-xs uppercase tracking-wide text-amber-800">
                    You've been invited by
                  </div>
                  <div className="font-medium" data-testid="parent-invite-student-name">
                    {decoded.student_name} (age {decoded.student_age})
                  </div>
                  <div className="text-xs text-stone-600">
                    for parent contact <strong>{decoded.parent_email}</strong>
                  </div>
                  <div className="text-xs text-stone-500 pt-1">
                    Expires{" "}
                    {new Date(decoded.expires_at).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    .
                  </div>
                </div>

                <form className="space-y-3" onSubmit={accept}>
                  <div className="space-y-1.5">
                    <Label htmlFor="parent-name">Your name</Label>
                    <Input
                      id="parent-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      data-testid="parent-invite-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="parent-password">
                      {decoded.existing_parent_account
                        ? "Password (for your existing account)"
                        : "Choose a password (min 8 chars)"}
                    </Label>
                    <Input
                      id="parent-password"
                      type="password"
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="parent-invite-password-input"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={reject}
                      disabled={submitting}
                      className="flex-1"
                      data-testid="parent-invite-reject-button"
                    >
                      Reject
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="parent-invite-accept-button"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : decoded.existing_parent_account ? (
                        <>
                          <CheckCircle2 className="mr-1 h-4 w-4" />
                          Accept &amp; sign in
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-1 h-4 w-4" />
                          Accept &amp; create account
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
